import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import { Message } from "semantic-ui-react";
import Link from "next/link";

const UkArticle = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/uk`;

  return (
    <MainLayout>
      <NextSeo
        title="9 Things You Didn't Know You Could Do In The UK | DAYMADE"
        description='Want to do something different closer to home? Explore some hidden gems of things to do in the UK - these local activities are unexpected and epic!'
        openGraph={{
          title: "9 Things You Didn't Know You Could Do In The UK | DAYMADE",
          description: 'Want to do something different closer to home? Explore some hidden gems of things to do in the UK - these local activities are unexpected and epic!',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/9-things-you-didnt-know-you-could-do-in-the-uk',
          images: [{
            url: `${IMAGE_DIRECTORY}/hero.jpg`,
            alt: 'man surfing blue artificial wave created at The Wave in Bristol'
          }]
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'instagram,instagrammable,bristol,The Wave,competition,local,travel,distance,destinations,holiday,prizes,getaway,countryside,museum'
        // }]}
      />
      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText='man surfing blue artificial wave created at The Wave in Bristol'
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
            9 Things You Didn't Know You Could Do <strong>In The UK</strong>
          </h1>

          <div className='separator' style={{ width: '80%', marginTop: 10 }} />

          <p>
            <span className='bigFirstLetter'>T</span>he best adventures don’t have to be on the other end of the world, sometimes they’re even closer than you think. The UK is home to plenty of hidden travel gems just waiting to be discovered. Mini-trips near home are the perfect way to spend a day, a weekend or staycation with friends and loved ones when you’re in the mood for an adventure. With so many worthy destinations, the UK is a hotspot for loads of exciting things to do. DAYMADE goes off the beaten path to bring you eight exciting things you didn’t know you could do in the UK. These thrilling places are truly worth a visit – and conveniently close to home.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt='arial shot of fields in Glastonbury with sun shining and shadows cast by the trees separating the fields, tall and symmetrical hill with pillar at top stands out in middle of shot'
            className='singleBlogImage'
          />

          <h4>
            Vineyards across the UK
          </h4>

          <p>
            Southern UK is the new Northern France. At least when it comes to wine. With a budding wine industry, the UK is the new kid on the block compared to traditional wine countries like France, Spain, Portugal and Italy. The good news for those who love to combine a passion for travel with a glass of vino is that you no longer have to pack your passport to have both. You can enjoy a weekend getaway wandering vineyards, popping bubbly and sampling local wines with your best gals or loved one(s) during a fun day out or weekend getaway trip. Swap your French Bordeaux for a crisp sparkling wine from the UK – you may even discover champagne that’s better than Champagne itself! The best part is you’ll feel like you’ve travelled to southern Europe without having to actually use any of your valuable annual leave. Cheers to that!
          </p>

          <h4>
            The Wave, Bristol
          </h4>

          <p>
            Save yourself the long journey to the ocean, you only need to travel a few miles north of Bristol’s city centre to go surfing! Bristol now has its own inland surfing lake (powered by renewable energy) called The Wave, a place where hobbyists and professionals alike are flocking to ride the waves. The Wave provides perfect surfing conditions for beginners, intermediates and advanced surfers all year round. Who needs a coastal beach when you’re guaranteed 1,000 new waves rolling in every hour?
          </p>

          <h4>
            The Grant Museum of Zoology, London
          </h4>

          <p>
            There’s a room in London, one that’s full of animal skeletons, various bizarre creepy crawlies, formaldehyde jars containing all sorts of specimens, stuffed animals and loads more. Just one single room, the Grant Museum of Zoology is a quirky little museum where you can totally geek out with specimens from all over the world. If you’re in the area and want to experience something you’ve never seen before, then this fascinating collection is absolutely worth a visit, even if you’re not that interested in zoology!
          </p>

          <div className='doubleImageContainer'>
            <img
              src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
              alt='shelf in The Grant Museum of Zoology showing various skulls and butterflies'
              className='doubleBlogImage'
            />

            <img
              src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
              alt='three people hiking across mountainous region in Wales with clouds casting patterns on the grassy hills'
              className='doubleBlogImage'
              style={{
                objectPosition: '50% 100%'
              }}
            />
          </div>

          <h4>
            Caves & Gorges, Cheddar
          </h4>

          <p>
            The village of Cheddar offers so much more than the delicious cheese by the same name. This sleepy yet lively town is home to one of Britain's most spectacular natural landmarks. The Cheddar Gorge and Caves is a wonderful destination for walking through subterranean stalactite show caves or climbing awe-inspiring cliffs. The terrain holds many fascinating clues about prehistoric times and is an international centre for caving and rock climbing.
          </p>

          <p>
            <span style={{ fontStyle: 'italic' }}>Did you know:</span> original Cheddar cheese has been produced here since the 12th century and is still to this day stored in the Cheddar Caves to mature. So, what are you waiting for? Nature, adventure, food – it’s high time to explore the rocky terrain, with a plate of original Cheddar cheese and a glass of (local) wine to round off another perfect day in Cheddar.
          </p>

          <h4>
            People Watching in Glastonbury
          </h4>

          <p>
            After enjoying the natural beauty of Cheddar, head over to Glastonbury for some alternative vibes. Originally a town with roots as a place of Christian pilgrimage, Glastonbury’s present reality is its reputation as the new-age capital of England. If you like new age, witchy things or are interested in myth or legend, you will be in your element. The best thing to do while in Glastonbury is to have a tea at one of the many cafés and people-watch the fascinating and wonderful looking locals – some dressed up as mythical creatures might even walk by. And if things get really new-age quirky, you may even be invited to join a druidic ceremony or witchy circle. Such a place may or may not be your cup of tea, but it’s definitely worth a visit to cross off the bucket list.
          </p>

          <h4>
            Mount Snowdon, Wales
          </h4>

          <p>
            It’s not quite Mt. Everest, but you can even risk altitude sickness in Wales! Mount Snowden is the highest mountain in Wales (1,085 metres or 3,560 feet) with breath-taking scenery that will help you leave any worries at the bottom. Strap on your hiking boots and hike one of the six official trails up the mountain, or if walking isn’t for you, then kick back and enjoy the scenery on a steam engine ride to the top. Once you reach the peak, you may even find fragments of shell fossils. The fossilised remains are due to the fact that five hundred million years ago Mount Snowdon was on the seabed. This mountain is a beautiful and majestic part of Wales’ history and geography with plenty of adventures waiting to be had.
          </p>

          <h4>
            The Freud Museum, Hampstead
          </h4>

          <p>
            Step into the world of the founder of psychoanalysis by exploring the Freud Museum, Sigmund Freud’s final home. The house where the Freuds lived after escaping the Nazi regime in Austria is quaint, beautiful and packed full of relics. The museum is not a museum of psychoanalysis but more a shrine to the life of Freud and his family – featuring furniture, collections, books, photos, paintings, family tree and all. Perhaps most impressive is seeing Freud's personal belongings including his infamous therapy couch. Definitely worth the experience (and price), the admission fee also gives visitors a free annual pass with your ticket.
          </p>

          <h4>
            Barrowland Park Album Pathway, Glasgow
          </h4>

          <p>
            Glasgow’s music scene is not to be overlooked when in the area. Take a stroll down the city’s musical memory lane at the Barrowland Park album pathway. Like a journey back in time to see the great and the good who have played at the Barrowland venue, Barrowland Park hosts a public art display known as Jim Lambie’s Album Pathway. Arranged like records on a shelf, Lambie’s album pathway is a listing of the musical acts that have performed at the nearby Barrowland concert venue since its opening in 1983. After this free tour, you’re sure to want to see what all the hype’s about by visiting a live gig at the Barrowland Ballroom in the evening… And next time you visit Glasgow, the concert you rocked out to will be added to the Park’s collection.
          </p>

          <h4>
            Scilly Isles, Archipelago in England
          </h4>

          <p>
            Paradise is practically right in your own backyard. The Isles of Scilly are an archipelago off the Cornish coast in southwest England and a fantastic place to get away from it all. Fringed by sandy beaches against a backdrop of Caribbean-coloured (but not temperatures!) sea, the islands are truly an ideal location for a few days away from the real world. The quiet, ‘everyone-knows-everyone’ island life vibe provides loads of variety, ranging from various kinds of water sports to visiting the Valhalla Museum and strolling around the nearly car-free island on foot. The Scilly Isles are the closest you’ll get to a bit of tropical island life in the UK when you’re not able to make the journey to the tropics.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt='green countryside landscape with rows of herbs in farm and grassy English hills in the background'
            className='singleBlogImage'
          />

          <p>
            Leave it to us at DAYMADE when you’re looking for something a bit more off the beaten path. These tips for local destinations are different than the typical, cliched destinations – and somewhere that fulfils your passion for travel without breaking the bank (or your annual holiday leave). DAYMADE’ list of the eight best alternative getaway destinations in the UK are only a hop, skip or a train ride away that will leave you with plenty to discover. Best of all, you don’t have to wait for summer holidays or plan a trip abroad to go on your next exciting adventure.
          </p>

          <Message>
            Every week we make many winners happy with cool local experiences. You only need to match 3 numbers in one of the DAYMADE {''}
            <Link href='/' style={{ margin: 0, fontWeight: 'bold' }}>
              Weekly Draws
            </Link>
            {''} to win a £25 prize, so the chances are in your favour — enter now and you may be amongst our next winners!
          </Message>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default UkArticle;
